import React, { useEffect, useState, useRef, useMemo } from "react";
import { Server, HardDrive, Plus, Trash2, X, Power, PowerOff, Pause, Play, RotateCw, Cloud, Database, FileDown, Camera, Copy as CopyIcon, ChevronDown, FolderTree, Download, KeyRound, Lock } from "lucide-react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { http, errMsg, apiBaseUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useWS } from "@/context/WSContext";
import ConnectStorageModal from "@/components/ConnectStorageModal";
import ConnectS3Modal from "@/components/storage/ConnectS3Modal";
import ConnectAzureModal from "@/components/storage/ConnectAzureModal";
import FsBrowser from "@/components/FsBrowser";
import SearchInput, { filterByName, useUrlSearchState } from "@/components/SearchInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { notifyDeviceCountChanged } from "@/lib/deviceCountEvents";

const ROLE_RANK = { viewer: 1, auditor: 2, engineer: 3, admin: 4, superadmin: 5 };

const AGENT_KIND = {
    vm: { label: "VM agent", icon: Server, color: "#5b5ef0" },
    pc: { label: "PC agent", icon: Server, color: "#5b5ef0" },
    server: { label: "Server agent", icon: Server, color: "#8b5ef0" },
    nas: { label: "NAS (SMB/NFS)", icon: HardDrive, color: "#00C2FF" },
    s3: { label: "Amazon S3", icon: Cloud, color: "#ff9900" },
    gcs: { label: "Google Cloud Storage", icon: Cloud, color: "#4285F4" },
    azure: { label: "Azure Blob Storage", icon: Cloud, color: "#0078D4" },
};

// ---- Devices-page tab taxonomy ----
//
// Buckets every device into a top-level tab (Windows / Linux / Servers /
// NAS) and a sub-tab within it. The installer lets the operator pick
// PC / Server / NAS Gateway at install time (see MoovByteAgent.iss), so
// `kind` is a real, meaningful signal here -- not a guess.
//
// "VMs" vs "Hardware": kind === "vm" is the operator's manual install-time
// pick, which the real installer never actually offers (only PC/Server/NAS
// Gateway) -- so it only ever populates for manually-added test/demo
// entries. Real agents are classified by hardware.is_virtual instead --
// HardwareInfoCollector.DetectVirtualization checks the SMBIOS
// manufacturer/model (VMware/Hyper-V/VirtualBox/KVM/Xen/cloud signatures),
// which a hypervisor controls regardless of what CPU/RAM the guest is
// configured with, unlike cpu_model/ram_total_gb which can coincidentally
// match a real PC with similar specs. See classifyAgent below.
const HARDWARE_VM_SUBTABS = [
    { key: "all", label: "All" },
    { key: "hardware", label: "Hardware" },
    { key: "vm", label: "VMs" },
];

// Sub-tabs with no backing connector at all -- kept here as the single
// place to add another reserved/placeholder brand slot in the future.
// Azure Cloud used to be one of these; it now has a real kind="azure"
// connector (SAS-token REST client, no Azure SDK dependency — see
// AzureBlobRestClient.cs), so it's no longer in this set.
const NAS_SUBTABS_WITHOUT_BACKEND = new Set();

const NAS_BRAND_SUBTABS = [
    { key: "all", label: "All" },
    { key: "Synology", label: "Synology" },
    { key: "QNAP", label: "QNAP" },
    { key: "NetApp", label: "NetApp" },
    { key: "TrueNAS", label: "TrueNAS" },
    { key: "Ubiquiti", label: "Ubiquiti" },
    { key: "UGreen", label: "UGreen" },
    { key: "AWS", label: "AWS" },
    { key: "Google Cloud", label: "Google Cloud" },
    { key: "Azure Cloud", label: "Azure Cloud" },
    { key: "Other", label: "Other" },
];

const TOP_TABS = [
    { key: "all", label: "All" },
    { key: "windows", label: "Windows" },
    { key: "linux", label: "Linux" },
    { key: "macos", label: "macOS" },
    { key: "servers", label: "Servers" },
    { key: "nas", label: "NAS" },
];

/** Best-effort brand bucket for a NAS-kind device from its (auto-detected
 * or operator-picked) vendor_hint string. Cloud kinds map directly since
 * there's no vendor ambiguity there. */
function classifyNasBrand(a) {
    if (a.kind === "s3") return "AWS";
    if (a.kind === "gcs") return "Google Cloud";
    if (a.kind === "azure") return "Azure Cloud";
    const v = (a.nas?.vendor_hint || "").toLowerCase();
    if (v.includes("synology")) return "Synology";
    if (v.includes("qnap")) return "QNAP";
    if (v.includes("netapp")) return "NetApp";
    if (v.includes("truenas") || v.includes("freenas")) return "TrueNAS";
    if (v.includes("ubiquiti") || v.includes("unifi")) return "Ubiquiti";
    if (v.includes("ugreen")) return "UGreen";
    return "Other";
}

function classifyAgent(a) {
    const kind = a.kind || "vm";
    if (kind === "nas" || kind === "s3" || kind === "gcs" || kind === "azure") {
        return { top: "nas", sub: classifyNasBrand(a) };
    }
    // kind === "vm" is the operator's manual install-time pick (never
    // actually offered by the real installer — see comment above). Real
    // agents now self-report hardware.is_virtual via HardwareInfoCollector's
    // SMBIOS manufacturer/model check, which is what most devices will
    // actually be classified on; kind=="vm" stays as a fallback for
    // manually-added test/demo entries with no hardware payload at all.
    const sub = (kind === "vm" || a.hardware?.is_virtual) ? "vm" : "hardware";
    if (kind === "server") return { top: "servers", sub };
    const os = (a.os || "").toLowerCase();
    if (os.includes("windows")) return { top: "windows", sub };
    if (/linux|ubuntu|debian|centos|rhel|fedora/.test(os)) return { top: "linux", sub };
    if (/mac ?os|darwin/.test(os)) return { top: "macos", sub };
    return { top: "other", sub };
}

// ---- Agent diagnostics widget ----
//
// Surfaces the "is the agent really doing what we think?" signals on
// each agent card so operators don't have to crack open MongoDB to
// decide whether a stuck migration is a frontend cache, a stale agent
// id, or an actual agent-side hang. Backend stamps these on every
// /api/agents/commands poll (see routers/agents.py::agent_commands).
function fmtAgo(iso) {
    if (!iso) return "—";
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return iso;
    const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
    if (sec < 60)   return `${sec}s ago`;
    if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
    return `${Math.round(sec / 86400)}d ago`;
}

// ---- Stuck-agent card ----
//
// Surfaces every agent the `stuck_agent_alert_loop` worker has flagged
// open right now. The worker reconciles every 30 s in the background;
// the "Reconcile now" CTA POSTs /api/agents/poll-stuck/reconcile so an
// operator who just restarted a hung agent doesn't have to wait up to
// 30 s for the alert to clear. Hidden when zero open alerts exist.
function StuckAgentsCard({ canManage }) {
    const [stuck, setStuck] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reconciling, setReconciling] = useState(false);
    const refresh = async () => {
        try {
            const { data } = await http.get("/agents/poll-stuck");
            setStuck(data || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    };
    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 15000);
        return () => clearInterval(iv);
    }, []);

    const reconcile = async () => {
        setReconciling(true);
        try {
            const { data } = await http.post("/agents/poll-stuck/reconcile");
            setStuck(data || []);
        } catch (e) {
            alert(errMsg(e));
        } finally {
            setReconciling(false);
        }
    };

    if (loading) return null;
    if (stuck.length === 0) return null;

    return (
        <div
            className="mb-5 border border-amber-500/40 bg-amber-500/5"
            data-testid="stuck-agents-card"
        >
            <div className="flex items-start justify-between gap-4 border-b border-amber-500/30 px-4 py-3">
                <div>
                    <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-amber-400">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
                        Agents not polling
                    </div>
                    <div className="mt-1 text-sm text-foreground">
                        {stuck.length} agent{stuck.length === 1 ? "" : "s"} flagged stuck —
                        likely service stopped, network blocked, or token revoked.
                    </div>
                </div>
                {canManage && (
                    <button
                        onClick={reconcile}
                        disabled={reconciling}
                        data-testid="stuck-agents-reconcile-btn"
                        className="inline-flex shrink-0 items-center gap-2 rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-amber-300 hover:bg-amber-500/20 disabled:opacity-50"
                    >
                        <RotateCw size={13} className={reconciling ? "animate-spin" : ""} />
                        {reconciling ? "Reconciling…" : "Reconcile now"}
                    </button>
                )}
            </div>
            <div className="divide-y divide-amber-500/15">
                {stuck.map((s) => (
                    <div key={s.alert_id} className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2.5 text-xs" data-testid={`stuck-agents-row-${s.agent_id}`}>
                        <div className="flex-1 min-w-[160px]">
                            <div className="font-medium text-foreground">{s.agent_name}</div>
                            <div className="font-mono text-[10px] text-muted-foreground">{s.agent_id.slice(0, 8)} · {s.agent_version || "—"}</div>
                        </div>
                        <div className="text-muted-foreground" title={s.last_commands_poll_at || ""}>
                            Last poll: <span className="text-foreground">{fmtAgo(s.last_commands_poll_at)}</span>
                        </div>
                        <div className="text-muted-foreground" title={s.last_heartbeat || ""}>
                            Last heartbeat: <span className="text-foreground">{fmtAgo(s.last_heartbeat)}</span>
                        </div>
                        <div className="text-muted-foreground" title={s.alerted_at}>
                            Alerted: <span className="text-foreground">{fmtAgo(s.alerted_at)}</span>
                        </div>
                        <Link
                            to={`/agents?diag=${s.agent_id}`}
                            className="text-amber-300 hover:text-amber-200 text-xs font-medium"
                            data-testid={`stuck-agents-open-diag-${s.agent_id}`}
                        >
                            Open diagnostics →
                        </Link>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---- Quantum-Safe Transfer capability badge + remote policy control ----
//
// `security_capabilities` / `security_policy` are self-reported by the C#
// agent on every register + heartbeat (see routers/agents.py::agent_heartbeat
// and agent-csharp HeartbeatService.cs). An agent build that predates this
// feature reports neither field, so the badge simply doesn't render for it
// — no false "not PQC-ready" claim, just nothing to show yet.
//
// The policy dropdown POSTs /agents/{id}/security-policy, which enqueues a
// "set-security-policy" command the agent picks up on its next commands
// poll (same mechanism as drain/undrain) — no new polling loop. The change
// is in-memory on the agent (CommandListener.PolicyOverride) and reverts to
// config.json's value on a service restart, so `security_policy_requested`
// vs `security_policy` on the agent doc distinguishes "asked for" from
// "confirmed active."
const STORAGE_CONNECTION_KINDS = new Set(["nas", "s3", "gcs", "azure"]);

// First step of "Connect storage" — picks which first-class connect modal
// to open next (ConnectStorageModal for NAS, ConnectS3Modal, ConnectAzureModal).
function StorageKindPicker({ onPick, onClose }) {
    const options = [
        { kind: "nas", label: "NAS (SMB / NFS)", icon: HardDrive, sub: "Synology, QNAP, NetApp, TrueNAS…" },
        { kind: "s3", label: "Amazon S3", icon: Cloud, sub: "or an S3-compatible provider" },
        { kind: "azure", label: "Azure Blob Storage", icon: Cloud, sub: "SAS-token authenticated" },
    ];
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" role="dialog">
            <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-5 shadow-2xl" data-testid="storage-kind-picker">
                <div className="mb-3 flex items-center justify-between">
                    <div className="font-display text-lg font-bold">Connect storage</div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-md border border-border p-1.5 hover:border-foreground/40"
                        aria-label="Close"
                    >
                        <X size={13} />
                    </button>
                </div>
                <div className="space-y-2">
                    {options.map(({ kind, label, icon: Icon, sub }) => (
                        <button
                            key={kind}
                            type="button"
                            onClick={() => onPick(kind)}
                            data-testid={`storage-kind-${kind}`}
                            className="flex w-full items-center gap-3 rounded-lg border border-border px-4 py-3 text-left hover:border-foreground/40"
                        >
                            <Icon size={16} className="shrink-0 text-muted-foreground" />
                            <div>
                                <div className="text-sm font-semibold">{label}</div>
                                <div className="text-[11px] text-muted-foreground">{sub}</div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function SecurityBadge({ agent, canManage, onChanged }) {
    const [busy, setBusy] = useState(false);
    const capable = (agent.security_capabilities || []).includes("QuantumSafeHybrid");

    // A connection record never registers/heartbeats as a running agent, so
    // it never reports security_capabilities and `capable` above is always
    // false — without this branch it looked identical to "unencrypted,"
    // when really its PQC status is inherited per-job from whichever real
    // gateway agent handles a transfer on its behalf.
    if (!capable && STORAGE_CONNECTION_KINDS.has(agent.kind)) {
        return (
            <div
                className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1"
                data-testid={`agent-security-${agent.id}`}
                title="This connection has no software of its own — encryption is inherited per-job from whichever real gateway agent connects to it and carries out the transfer."
            >
                <Lock size={10} className="shrink-0 text-muted-foreground" />
                <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">
                    Gateway-secured
                </span>
            </div>
        );
    }
    if (!capable) return null;

    const policy = agent.security_policy || "Preferred";
    const pending = agent.security_policy_requested && agent.security_policy_requested !== agent.security_policy;

    const setPolicy = async (e) => {
        const next = e.target.value;
        if (next === policy) return;
        setBusy(true);
        try {
            await http.post(`/agents/${agent.id}/security-policy`, { policy: next });
            onChanged?.();
        } catch (err) {
            alert(errMsg(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1"
            data-testid={`agent-security-${agent.id}`}
            title={
                pending
                    ? `Change to ${agent.security_policy_requested} requested — applies on the agent's next command poll (~5s)`
                    : "Quantum-Safe Transfer capable (hybrid X25519 + ML-KEM-768)"
            }
        >
            <Lock size={10} className="shrink-0 text-[hsl(var(--success))]" />
            <span className="font-mono text-[9px] uppercase tracking-widest text-muted-foreground">PQC</span>
            {canManage ? (
                <select
                    value={policy}
                    onChange={setPolicy}
                    disabled={busy}
                    data-testid={`agent-security-policy-select-${agent.id}`}
                    className="ml-auto rounded-sm border border-border bg-background px-1 py-0.5 font-mono text-[9px] uppercase tracking-widest text-foreground disabled:opacity-50"
                >
                    <option value="Preferred">Preferred</option>
                    <option value="Required">Required</option>
                    <option value="Disabled">Disabled</option>
                </select>
            ) : (
                <span className="ml-auto font-mono text-[9px] uppercase tracking-widest text-foreground">{policy}</span>
            )}
            {pending && <span className="text-[hsl(var(--warning))]" title="Awaiting confirmation from the agent">•</span>}
        </div>
    );
}

function AgentDiagnostics({ agent, autoOpen = false }) {
    const [open, setOpen] = useState(autoOpen);
    const [copied, setCopied] = useState(false);
    const rootRef = useRef(null);

    // When the parent toggles `autoOpen` (operator landed via /agents?diag=ID),
    // expand AND smooth-scroll the row into view. Once-only — subsequent
    // operator interaction with the chevron isn't overridden.
    useEffect(() => {
        if (autoOpen) {
            setOpen(true);
            const node = rootRef.current;
            if (node) {
                // Defer to next frame so layout settles before measuring.
                requestAnimationFrame(() => {
                    node.scrollIntoView({ behavior: "smooth", block: "center" });
                });
            }
        }
    }, [autoOpen]);

    const copyId = async () => {
        try {
            await navigator.clipboard.writeText(agent.id);
            setCopied(true);
            setTimeout(() => setCopied(false), 1200);
        } catch { /* ignore */ }
    };
    return (
        <div ref={rootRef} className="mt-2 border-t border-border/60 pt-2" data-testid={`agent-diag-${agent.id}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
                data-testid={`agent-diag-toggle-${agent.id}`}
            >
                <span>Diagnostics</span>
                <ChevronDown size={11} className={open ? "rotate-180 transition" : "transition"} />
            </button>
            {open && (
                <dl className="mt-2 grid grid-cols-[7rem_1fr] gap-y-1 font-mono text-[10px]">
                    <dt className="text-muted-foreground">Running v</dt>
                    <dd className="text-foreground" data-testid={`agent-diag-version-${agent.id}`}>
                        v{agent.version || "—"}
                    </dd>

                    <dt className="text-muted-foreground">Agent ID</dt>
                    <dd className="flex items-center gap-1.5">
                        <code className="truncate text-foreground" title={agent.id}>{agent.id}</code>
                        <button
                            type="button"
                            onClick={copyId}
                            title={copied ? "copied" : "copy"}
                            className="text-muted-foreground hover:text-foreground"
                            data-testid={`agent-diag-copy-id-${agent.id}`}
                        >
                            <CopyIcon size={10} />
                        </button>
                        {copied && <span className="text-[hsl(var(--success))]">✓</span>}
                    </dd>

                    <dt className="text-muted-foreground">Heartbeat</dt>
                    <dd className="text-foreground" data-testid={`agent-diag-heartbeat-${agent.id}`}>
                        {fmtAgo(agent.last_heartbeat)}
                    </dd>

                    <dt className="text-muted-foreground">Claim poll</dt>
                    <dd className="text-foreground" data-testid={`agent-diag-claim-${agent.id}`}>
                        {fmtAgo(agent.last_claim_poll_at)}
                        {agent.claim_poll_count != null && (
                            <span className="ml-1 text-muted-foreground">({agent.claim_poll_count}×)</span>
                        )}
                    </dd>

                    <dt className="text-muted-foreground">Cmd poll</dt>
                    <dd className="text-foreground" data-testid={`agent-diag-cmdpoll-${agent.id}`}>
                        {fmtAgo(agent.last_commands_poll_at)}
                        {agent.commands_poll_count != null && (
                            <span className="ml-1 text-muted-foreground">({agent.commands_poll_count}×)</span>
                        )}
                    </dd>

                    <dt className="text-muted-foreground">Last cmd</dt>
                    <dd className="text-foreground" data-testid={`agent-diag-lastcmd-${agent.id}`}>
                        {agent.last_command_action ? (
                            <>
                                <span className="text-[#5b5ef0]">{agent.last_command_action}</span>
                                <span className="ml-1 text-muted-foreground">{fmtAgo(agent.last_command_at)}</span>
                            </>
                        ) : (
                            <span className="text-muted-foreground">never</span>
                        )}
                    </dd>
                </dl>
            )}
        </div>
    );
}

export default function Agents() {
    const { user } = useAuth();
    const { subscribe } = useWS();
    const [agents, setAgents] = useState([]);
    const [search, setSearch] = useUrlSearchState("q");
    // Deliberately NOT two separate useUrlSearchState calls here: changing
    // the top tab must also clear the sub tab in the same navigation.
    // react-router's setSearchParams reads the CURRENT location at the
    // moment it's called, so two sequential calls in one handler each
    // build their own URLSearchParams from the same pre-click snapshot --
    // the second call's result clobbers the first's, and the "type"
    // change is silently lost. One combined update avoids that race.
    const [tabParams, setTabParams] = useSearchParams();
    const topTab = tabParams.get("type") || "all";
    const subTab = tabParams.get("sub") || "all";
    const changeTopTab = (key) => {
        setTabParams((prev) => {
            const sp = new URLSearchParams(prev);
            if (key === "all") sp.delete("type"); else sp.set("type", key);
            sp.delete("sub");
            return sp;
        }, { replace: true });
    };
    const changeSubTab = (key) => {
        setTabParams((prev) => {
            const sp = new URLSearchParams(prev);
            if (key === "all") sp.delete("sub"); else sp.set("sub", key);
            return sp;
        }, { replace: true });
    };
    const [showCreate, setShowCreate] = useState(false);
    const [showStorage, setShowStorage] = useState(null); // null | "source" | "destination" — role for a NEW connection
    const [storageKindPick, setStorageKindPick] = useState(null); // set once a kind is chosen for showStorage: "nas" | "s3" | "azure"
    const [editStorage, setEditStorage] = useState(null); // agent obj being edited | null
    const [browseAgent, setBrowseAgent] = useState(null); // agent obj | null
    const [newToken, setNewToken] = useState(null);
    const [newStorage, setNewStorage] = useState(null); // { name } | null — NAS connection just saved

    // Deep-link target: /agents?diag=<agent_id> auto-opens that agent's
    // Diagnostics drawer and scrolls the card into view. Used by the
    // StuckAgentsCard ("Open diagnostics →") + can be pasted into Slack
    // for cross-team triage. URL is left untouched after the scroll so
    // the link stays shareable.
    const diagAgentId = tabParams.get("diag");

    const canManage = (ROLE_RANK[user?.role] || 0) >= ROLE_RANK.engineer;
    const canDelete = user?.role === "admin" || user?.role === "superadmin";
    // Matches ApiTokens.jsx's own canDeploy gate — the Download Agent tab
    // it links to falls back to the Tokens tab for anyone below admin.
    const canDeployAgent = user?.role === "admin" || user?.role === "superadmin";

    const refresh = async () => {
        try {
            const { data } = await http.get("/agents");
            setAgents(data);
            // Every add/delete/connect action on this page already calls
            // refresh() -- piggyback the plan-limit banner's refresh here
            // too, so "1/10 devices" updates immediately instead of
            // waiting up to 60s for its own poll.
            notifyDeviceCountChanged();
        } catch {
            /* ignore */
        }
    };

    const control = async (id, action) => {
        try {
            await http.post(`/agents/${id}/control`, { action });
            refresh();
        } catch (e) {
            alert(errMsg(e));
        }
    };

    // Navigate to the PC Capture & Apply wizard with this agent pre-selected
    // as the source. Read by Migrations.jsx via useSearchParams.
    const navigate = useNavigate();
    const startPCMigration = (agentId) => {
        navigate(`/migrations?source=${encodeURIComponent(agentId)}`);
    };

    const downloadInventory = async (agent) => {
        try {
            // Use fetch directly so we can stream the PDF blob (axios-interceptor
            // http client is JSON-only). Auth cookies ride along automatically.
            const base = apiBaseUrl();
            const resp = await fetch(`${base}/api/agents/${agent.id}/report.pdf`, {
                credentials: "include",
            });
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const blob = await resp.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `moovbytes-agent-${(agent.name || agent.id).replace(/\s+/g, "_")}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(a.href);
        } catch (e) {
            alert(`Could not download inventory: ${e.message || e}`);
        }
    };

    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 5000);
        return () => clearInterval(iv);
    }, []);

    useEffect(() => {
        const unsub = subscribe((m) => {
            if (m.event === "agent.heartbeat") {
                setAgents((prev) =>
                    prev.map((a) =>
                        a.id === m.data.agent_id
                            ? { ...a, ...m.data, last_heartbeat: new Date().toISOString() }
                            : a
                    )
                );
            }
        });
        return unsub;
    }, [subscribe]);

    const remove = async (id) => {
        if (!window.confirm("Delete this agent?")) return;
        try {
            await http.delete(`/agents/${id}`);
            refresh();
        } catch (e) {
            alert(errMsg(e));
        }
    };

    // Visible-list memo — filters by search query across the fields an
    // operator is most likely to type ("ny-prod-01", a hostname, or the
    // truncated agent id). Empty query is a no-op (returns full list).
    const searchFiltered = React.useMemo(
        () => filterByName(agents, search, ["name", "hostname", "public_address", "id"]),
        [agents, search],
    );

    // Classify once per render of the search-filtered set, then layer the
    // top-tab / sub-tab filters on top. Counts are computed at each stage
    // so tab labels reflect "how many would show if I clicked this,"
    // not just the currently-selected tab's own count.
    const classifiedAgents = React.useMemo(
        () => searchFiltered.map((a) => ({ ...a, _cls: classifyAgent(a) })),
        [searchFiltered],
    );

    const topTabCounts = React.useMemo(() => {
        const counts = { all: classifiedAgents.length };
        for (const a of classifiedAgents) counts[a._cls.top] = (counts[a._cls.top] || 0) + 1;
        return counts;
    }, [classifiedAgents]);
    const hasOtherTab = (topTabCounts.other || 0) > 0;

    const topFiltered = React.useMemo(
        () => (topTab === "all" ? classifiedAgents : classifiedAgents.filter((a) => a._cls.top === topTab)),
        [classifiedAgents, topTab],
    );

    const subTabDefs = topTab === "nas" ? NAS_BRAND_SUBTABS : HARDWARE_VM_SUBTABS;
    const subTabCounts = React.useMemo(() => {
        const counts = { all: topFiltered.length };
        for (const a of topFiltered) counts[a._cls.sub] = (counts[a._cls.sub] || 0) + 1;
        return counts;
    }, [topFiltered]);

    const visibleAgents = React.useMemo(
        () => (subTab === "all" ? topFiltered : topFiltered.filter((a) => a._cls.sub === subTab)),
        [topFiltered, subTab],
    );

    return (
        <div className="mx-auto max-w-[1600px] p-6">
            <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                        Fleet
                    </div>
                    <h1 className="mt-1 font-display text-4xl font-bold tracking-tight">Devices</h1>
                </div>
                {canManage && (
                    <div className="flex flex-wrap items-center gap-2">
                        {canDeployAgent && (
                            <Link
                                to="/access-tokens?tab=deploy-agent"
                                data-testid="agents-download-agent-btn"
                                className="inline-flex items-center gap-2 rounded-sm bg-brand px-3 py-2 text-xs font-semibold uppercase tracking-widest text-brand-foreground hover:bg-brand-dark"
                            >
                                <Download size={13} /> Download agent
                            </Link>
                        )}
                        <button
                            onClick={() => setShowStorage("source")}
                            data-testid="agents-connect-storage-btn"
                            className="inline-flex items-center gap-2 rounded-sm border border-[#00C2FF]/50 bg-[#00C2FF]/10 px-3 py-2 text-xs font-semibold uppercase tracking-widest text-[#00C2FF] hover:bg-[#00C2FF]/20"
                        >
                            <HardDrive size={13} /> Connect storage
                        </button>
                        <button
                            onClick={() => setShowCreate(true)}
                            data-testid="agents-create-btn"
                            title="Manually add a device record without installing real agent software — useful for demos or planning"
                            className="inline-flex items-center gap-2 rounded-sm border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                        >
                            <Plus size={13} /> Add manually
                        </button>
                    </div>
                )}
            </div>

            <StuckAgentsCard canManage={canManage} />

            <div className="mb-3 flex flex-wrap items-center gap-1 border-b border-border" data-testid="agents-top-tabs" role="tablist">
                {TOP_TABS.concat(hasOtherTab ? [{ key: "other", label: "Other" }] : []).map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        onClick={() => changeTopTab(t.key)}
                        data-testid={`agents-tab-${t.key}`}
                        aria-selected={topTab === t.key}
                        className={[
                            "border-b-2 px-3 py-2 font-mono text-[11px] font-semibold uppercase tracking-widest transition-colors",
                            topTab === t.key
                                ? "border-brand text-foreground"
                                : "border-transparent text-muted-foreground hover:text-foreground",
                        ].join(" ")}
                    >
                        {t.label} <span className="text-muted-foreground">{topTabCounts[t.key] || 0}</span>
                    </button>
                ))}
            </div>

            {topTab !== "all" && (
                <div className="mb-3 flex flex-wrap items-center gap-1.5" data-testid="agents-sub-tabs" role="tablist">
                    {subTabDefs.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => changeSubTab(t.key)}
                            data-testid={`agents-subtab-${t.key.replace(/\s+/g, "-").toLowerCase()}`}
                            aria-selected={subTab === t.key}
                            className={[
                                "rounded-full border px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest transition-colors",
                                subTab === t.key
                                    ? "border-[#00C2FF]/60 bg-[#00C2FF]/10 text-[#00C2FF]"
                                    : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground",
                            ].join(" ")}
                        >
                            {t.label} <span className="opacity-70">{subTabCounts[t.key] || 0}</span>
                        </button>
                    ))}
                </div>
            )}

            <div className="mb-4 flex items-center justify-between gap-3">
                <SearchInput
                    value={search}
                    onChange={setSearch}
                    placeholder="Search devices by name, hostname, or address…"
                    testid="agents-search-input"
                    className="w-full max-w-sm"
                />
                <div
                    className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground"
                    data-testid="agents-search-count"
                >
                    {visibleAgents.length} of {agents.length}
                </div>
            </div>

            {visibleAgents.length === 0 && topTab === "nas" && NAS_SUBTABS_WITHOUT_BACKEND.has(subTab) ? (
                <div className="mb-3 rounded-xl border border-dashed border-border p-8 text-center" data-testid="agents-tab-empty-state">
                    <div className="text-sm font-semibold text-foreground">{subTab} isn&apos;t connected yet</div>
                    <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                        This tab is reserved for it, but MoovBytes doesn&apos;t have an {subTab} connector built yet —
                        Amazon S3 and Google Cloud Storage are the cloud backends supported today. Ask if you'd
                        like {subTab} support added.
                    </p>
                </div>
            ) : agents.length > 0 && visibleAgents.length === 0 && (
                <div className="mb-3 rounded-xl border border-dashed border-border p-8 text-center" data-testid="agents-tab-empty-state">
                    <div className="text-sm font-semibold text-foreground">No devices in this view</div>
                    <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                        Nothing matches the current tab{search ? " and search" : ""}. Try a different tab or clear the filter.
                    </p>
                    <button
                        type="button"
                        onClick={() => { changeTopTab("all"); setSearch(""); }}
                        className="mt-3 inline-flex items-center gap-2 rounded-sm border border-border px-3 py-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:border-foreground/40 hover:text-foreground"
                    >
                        Clear filters
                    </button>
                </div>
            )}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4" data-testid="agents-grid">
                {visibleAgents.map((a) => {
                    const kind = AGENT_KIND[a.kind] || AGENT_KIND.vm;
                    const KindIcon = kind.icon;
                    const isCloud = a.kind === "s3" || a.kind === "gcs" || a.kind === "azure";
                    const isNas = a.kind === "nas";
                    return (
                    <div
                        key={a.id}
                        className="group flex flex-col overflow-hidden rounded-lg border border-border bg-surface shadow-sm transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-md"
                    >
                        <div className="flex items-center justify-between gap-2 border-b border-border/60 px-3 py-2">
                            <div className="flex min-w-0 items-center gap-1.5">
                                <span
                                    className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded"
                                    style={{ background: `${kind.color}1a`, color: kind.color }}
                                >
                                    <KindIcon size={11} />
                                </span>
                                <span className="truncate font-display text-[13px] font-semibold leading-tight">
                                    {a.name}
                                </span>
                            </div>
                            <span
                                className={[
                                    "mb-pill",
                                    a.status === "restarting"
                                        ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]"
                                        : a.enabled === false
                                          ? "border-muted-foreground text-muted-foreground"
                                          : a.drain
                                            ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]"
                                            : a.status === "online"
                                              ? "border-[hsl(var(--success))] text-[hsl(var(--success))]"
                                              : a.status === "stale"
                                                ? "border-[hsl(var(--warning))] text-[hsl(var(--warning))]"
                                                : "border-[hsl(var(--destructive))] text-[hsl(var(--destructive))]",
                                ].join(" ")}
                                data-testid={`agent-status-${a.id}`}
                                title={
                                    a.status === "stale"
                                        ? `Agent missed its last heartbeat — still within the grace window. Last seen: ${a.last_heartbeat || "—"}`
                                        : undefined
                                }
                            >
                                <span
                                    className={[
                                        "mb-status-dot",
                                        a.status === "restarting"
                                            ? "bg-[hsl(var(--warning))] animate-pulse-dot"
                                            : a.enabled === false
                                              ? "bg-muted-foreground"
                                              : a.drain
                                                ? "bg-[hsl(var(--warning))]"
                                                : a.status === "online"
                                                  ? "bg-[hsl(var(--success))] animate-pulse-dot"
                                                  : a.status === "stale"
                                                    ? "bg-[hsl(var(--warning))] animate-pulse-dot"
                                                    : "bg-[hsl(var(--destructive))]",
                                    ].join(" ")}
                                />
                                {a.status === "restarting"
                                    ? "restarting"
                                    : a.enabled === false
                                      ? "disabled"
                                      : a.drain
                                        ? "draining"
                                        : a.status === "stale"
                                          ? "stale"
                                          : a.status}
                            </span>
                        </div>
                        <div className="flex-1 space-y-2 px-3 py-2.5">
                            {isNas ? (
                                <>
                                    <NasCardBody agent={a} />
                                    <SecurityBadge agent={a} canManage={canManage} onChanged={refresh} />
                                </>
                            ) : isCloud ? (
                                <>
                                    <div className="flex items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1">
                                        <Database size={10} style={{ color: kind.color }} />
                                        <code
                                            className="truncate font-mono text-[10px]"
                                            title={a.public_address}
                                            data-testid={`agent-cloud-uri-${a.id}`}
                                        >
                                            {a.public_address}
                                        </code>
                                    </div>
                                    <div
                                        className="truncate font-mono text-[10px] text-muted-foreground"
                                        title={[a.region && `Region: ${a.region}`, a.credential_ref && `Cred: ${a.credential_ref}`, `last heartbeat ${a.last_heartbeat || "—"}`].filter(Boolean).join(" · ")}
                                    >
                                        {[a.region, a.credential_ref].filter(Boolean).join(" · ") || "Credentials resolved agent-side"}
                                        <span className="mx-1 text-border">·</span>
                                        {(a.last_heartbeat || "").slice(11, 19) || "—"}
                                    </div>
                                    <SecurityBadge agent={a} canManage={canManage} onChanged={refresh} />
                                </>
                            ) : (
                                <>
                                    <div className="grid grid-cols-3 gap-2.5">
                                        <Metric label="CPU" value={a.cpu_percent} />
                                        <Metric label="MEM" value={a.mem_percent} />
                                        <Metric label="DSK" value={a.disk_percent} accent />
                                    </div>
                                    <div
                                        className="truncate font-mono text-[10px] text-muted-foreground"
                                        title={`${a.public_address || `${a.hostname}:${a.listen_port || 9443}`} · ${a.os} · last heartbeat ${a.last_heartbeat || "—"}`}
                                        data-testid={`agent-meta-${a.id}`}
                                    >
                                        {a.public_address || `${a.hostname}:${a.listen_port || 9443}`}
                                        <span className="mx-1 text-border">·</span>
                                        {a.os}
                                        <span className="mx-1 text-border">·</span>
                                        {(a.last_heartbeat || "").slice(11, 19) || "—"}
                                    </div>
                                    {a.tls_fingerprint && (
                                        <div
                                            className="truncate font-mono text-[9.5px] text-muted-foreground"
                                            title={a.tls_fingerprint}
                                            data-testid={`agent-tls-${a.id}`}
                                        >
                                            TLS <span className="text-foreground">{a.tls_fingerprint.slice(0, 20)}…</span>
                                        </div>
                                    )}
                                    <SecurityBadge agent={a} canManage={canManage} onChanged={refresh} />
                                </>
                            )}
                            {!isCloud && !isNas && (
                                <AgentDiagnostics
                                    agent={a}
                                    autoOpen={diagAgentId === a.id}
                                />
                            )}
                        </div>
                        {canManage && (
                            <div className="flex flex-wrap items-center justify-end gap-1 border-t border-border/60 bg-background/30 px-2 py-1">
                                {a.enabled === false ? (
                                    <AgentAction
                                        label="Enable"
                                        icon={Power}
                                        onClick={() => control(a.id, "enable")}
                                        testId={`agent-enable-${a.id}`}
                                        positive
                                    />
                                ) : (
                                    <AgentAction
                                        label="Disable"
                                        icon={PowerOff}
                                        onClick={() => control(a.id, "disable")}
                                        testId={`agent-disable-${a.id}`}
                                    />
                                )}
                                {a.drain ? (
                                    <AgentAction
                                        label="Undrain"
                                        icon={Play}
                                        onClick={() => control(a.id, "undrain")}
                                        testId={`agent-undrain-${a.id}`}
                                        positive
                                    />
                                ) : (
                                    <AgentAction
                                        label="Drain"
                                        icon={Pause}
                                        onClick={() => control(a.id, "drain")}
                                        testId={`agent-drain-${a.id}`}
                                    />
                                )}
                                <AgentAction
                                    label="Restart"
                                    icon={RotateCw}
                                    onClick={() => control(a.id, "restart")}
                                    testId={`agent-restart-${a.id}`}
                                />
                                {/win/i.test(a.os || "") && a.status === "online" && (
                                    <AgentAction
                                        label="Migrate"
                                        icon={Camera}
                                        onClick={() => startPCMigration(a.id)}
                                        testId={`agent-migrate-${a.id}`}
                                        positive
                                    />
                                )}
                                <AgentAction
                                    label="PDF"
                                    icon={FileDown}
                                    onClick={() => downloadInventory(a)}
                                    testId={`agent-pdf-${a.id}`}
                                />
                                {(isNas || a.kind === "s3" || a.kind === "azure") && (
                                    <AgentAction
                                        label="Edit credentials"
                                        icon={KeyRound}
                                        onClick={() => setEditStorage(a)}
                                        testId={`agent-edit-storage-${a.id}`}
                                    />
                                )}
                                {a.status === "online" && (
                                    <AgentAction
                                        label="Browse"
                                        icon={FolderTree}
                                        onClick={() => setBrowseAgent(a)}
                                        testId={`agent-browse-${a.id}`}
                                    />
                                )}
                                {canDelete && (
                                    <AgentAction
                                        label="Delete"
                                        icon={Trash2}
                                        onClick={() => remove(a.id)}
                                        testId={`agent-delete-${a.id}`}
                                        danger
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    );
                })}
                {agents.length === 0 && (
                    <div className="col-span-full rounded-xl border border-dashed border-border p-10 text-center" data-testid="agents-empty-state">
                        <div className="text-sm font-semibold text-foreground">No devices yet</div>
                        <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
                            Install the MoovBytes agent on a real PC, server, or NAS to start migrating —
                            it registers itself here within seconds of starting up.
                        </p>
                        {canDeployAgent && (
                            <Link
                                to="/access-tokens?tab=deploy-agent"
                                data-testid="agents-empty-download-agent-btn"
                                className="mt-4 inline-flex items-center gap-2 rounded-sm bg-brand px-4 py-2 text-xs font-semibold uppercase tracking-widest text-brand-foreground hover:bg-brand-dark"
                            >
                                <Download size={13} /> Download agent
                            </Link>
                        )}
                    </div>
                )}
            </div>

            {showStorage && !storageKindPick && (
                <StorageKindPicker
                    onPick={(kind) => setStorageKindPick(kind)}
                    onClose={() => setShowStorage(null)}
                />
            )}
            {showStorage && storageKindPick === "nas" && (
                <ConnectStorageModal
                    role={showStorage}
                    onClose={() => { setShowStorage(null); setStorageKindPick(null); }}
                    onSaved={(a) => {
                        setShowStorage(null);
                        setStorageKindPick(null);
                        // NAS connections have no software of their own — nothing is ever
                        // installed "on" the host, so they never get the agent-token /
                        // install-instructions modal below. A migration built against this
                        // connection will ask which real agent should do the mounting.
                        setNewStorage({ name: a.name });
                        refresh();
                    }}
                />
            )}
            {showStorage && storageKindPick === "s3" && (
                <ConnectS3Modal
                    role={showStorage}
                    onClose={() => { setShowStorage(null); setStorageKindPick(null); }}
                    onSaved={(a) => {
                        setShowStorage(null);
                        setStorageKindPick(null);
                        setNewStorage({ name: a.name });
                        refresh();
                    }}
                />
            )}
            {showStorage && storageKindPick === "azure" && (
                <ConnectAzureModal
                    role={showStorage}
                    onClose={() => { setShowStorage(null); setStorageKindPick(null); }}
                    onSaved={(a) => {
                        setShowStorage(null);
                        setStorageKindPick(null);
                        setNewStorage({ name: a.name });
                        refresh();
                    }}
                />
            )}
            {editStorage && editStorage.kind === "s3" && (
                <ConnectS3Modal
                    editAgent={editStorage}
                    onClose={() => setEditStorage(null)}
                    onSaved={() => {
                        setEditStorage(null);
                        refresh();
                    }}
                />
            )}
            {editStorage && editStorage.kind === "azure" && (
                <ConnectAzureModal
                    editAgent={editStorage}
                    onClose={() => setEditStorage(null)}
                    onSaved={() => {
                        setEditStorage(null);
                        refresh();
                    }}
                />
            )}
            {editStorage && editStorage.kind === "nas" && (
                <ConnectStorageModal
                    editAgent={editStorage}
                    onClose={() => setEditStorage(null)}
                    onSaved={() => {
                        setEditStorage(null);
                        refresh();
                    }}
                />
            )}
            {showCreate && (
                <CreateAgentModal
                    onClose={() => setShowCreate(false)}
                    onCreated={(a) => {
                        setShowCreate(false);
                        setNewToken({ name: a.name, token: a.token });
                        refresh();
                    }}
                />
            )}
            {newToken && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    data-testid="agent-token-modal"
                    role="dialog"
                >
                    <div className="w-full max-w-md border border-border bg-surface p-5">
                        <div className="flex items-center justify-between">
                            <div className="font-display text-lg font-bold">Agent token</div>
                            <button
                                onClick={() => setNewToken(null)}
                                className="rounded-sm border border-border p-1.5 hover:border-foreground/40"
                                data-testid="agent-token-close"
                            >
                                <X size={13} />
                            </button>
                        </div>
                        <div className="mt-3 text-xs text-muted-foreground">
                            Copy this token and install on host <span className="font-semibold">{newToken.name}</span>.
                            It will not be shown again.
                        </div>
                        <pre
                            className="mt-3 whitespace-pre-wrap break-all rounded-sm border border-border bg-background p-3 font-mono text-xs"
                            data-testid="agent-token-value"
                        >
                            {newToken.token}
                        </pre>
                    </div>
                </div>
            )}
            {newStorage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    data-testid="storage-connected-modal"
                    role="dialog"
                >
                    <div className="w-full max-w-md border border-border bg-surface p-5">
                        <div className="flex items-center justify-between">
                            <div className="font-display text-lg font-bold">Storage connected</div>
                            <button
                                onClick={() => setNewStorage(null)}
                                className="rounded-sm border border-border p-1.5 hover:border-foreground/40"
                                data-testid="storage-connected-close"
                            >
                                <X size={13} />
                            </button>
                        </div>
                        <div className="mt-3 text-xs text-muted-foreground">
                            <span className="font-semibold text-foreground">{newStorage.name}</span> is saved as a
                            storage connection — there&apos;s nothing to install on it. When you build a migration
                            using it, you&apos;ll pick one of your installed PC/server agents to do the connecting
                            and transferring on its behalf.
                        </div>
                    </div>
                </div>
            )}
            {browseAgent && (
                <FsBrowserDialog agent={browseAgent} agents={agents} onClose={() => setBrowseAgent(null)} />
            )}
        </div>
    );
}

// A "nas"-kind (or s3/gcs) agent record is just stored connection details —
// nothing ever installs software on it, so nothing ever polls for fs-list
// commands with that identity. Only these kinds can actually run fs-list.
const FS_BROWSE_CONNECTION_KINDS = new Set(["nas", "s3", "gcs", "azure"]);

// ---- FsBrowser dialog wrapper -----------------------------------------------
// Hosts the FsBrowser inside a Dialog. A real agent gets the Local/NAS
// toggle (its own drives, or shares it already has locally mapped). A NAS
// connection record has no filesystem of its own to browse directly —
// it needs a real installed agent to connect to it on its behalf first.
function FsBrowserDialog({ agent, agents, onClose }) {
    const isConnectionRecord = FS_BROWSE_CONNECTION_KINDS.has(agent.kind);
    const [kind, setKind] = useState("local");
    const [workerId, setWorkerId] = useState("");
    const realAgents = useMemo(
        () => (agents || []).filter((a) => !FS_BROWSE_CONNECTION_KINDS.has(a.kind)),
        [agents],
    );

    // With exactly one real agent installed there's no actual choice to
    // make — requiring a manual pick just looked like the dialog was
    // stuck showing "a PC" and nothing else. Auto-select it; the
    // dropdown still lets the operator switch if more agents show up
    // later.
    useEffect(() => {
        if (!workerId && realAgents.length === 1) setWorkerId(realAgents[0].id);
    }, [workerId, realAgents]);

    if (isConnectionRecord) {
        return (
            <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
                <DialogContent className="max-w-2xl" data-testid="agent-browse-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <FolderTree size={18} /> Browse storage on {agent.name || agent.hostname}
                        </DialogTitle>
                        <DialogDescription>
                            This is a storage connection, not installed software — it can&apos;t list its
                            own files. Pick one of your installed PC/server agents to connect to it and
                            browse on its behalf.
                        </DialogDescription>
                    </DialogHeader>
                    <select
                        value={workerId}
                        onChange={(e) => setWorkerId(e.target.value)}
                        data-testid="agent-browse-worker-select"
                        className="mb-3 w-full rounded border border-zinc-800 bg-zinc-900/40 px-3 py-2 text-sm"
                    >
                        <option value="">Pick a worker agent…</option>
                        {realAgents.map((a) => (
                            <option key={a.id} value={a.id}>
                                {`${a.name || a.hostname || a.id.slice(0, 8)} (${a.kind || "vm"})`}
                            </option>
                        ))}
                    </select>
                    {workerId ? (
                        <FsBrowser agentId={agent.id} kind={agent.kind === "azure" ? "azure" : "nas"} workerAgentId={workerId} key={workerId} />
                    ) : (
                        <div className="rounded border border-dashed border-zinc-800 p-6 text-center text-xs text-zinc-500">
                            {realAgents.length === 0
                                ? "No installed agents available — deploy the MoovBytes agent on a PC or server first."
                                : "Pick a worker agent above to start browsing."}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="agent-browse-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FolderTree size={18} /> Browse storage on {agent.name || agent.hostname}
                    </DialogTitle>
                    <DialogDescription>
                        The agent runs the directory listing on its own host (NAS shares resolve via the
                        agent's user session, not the control plane). Switch to NAS to enumerate mapped
                        SMB share roots.
                    </DialogDescription>
                </DialogHeader>
                <div className="mb-3 inline-flex rounded border border-zinc-800 bg-zinc-900/40 p-0.5" role="tablist">
                    {[
                        { v: "local", label: "Local drives" },
                        { v: "nas",   label: "NAS shares" },
                    ].map((t) => (
                        <button
                            key={t.v}
                            onClick={() => setKind(t.v)}
                            data-testid={`agent-browse-kind-${t.v}`}
                            role="tab"
                            aria-selected={kind === t.v}
                            className={[
                                "rounded px-3 py-1 text-xs font-medium",
                                kind === t.v ? "bg-blue-500 text-white" : "text-zinc-400 hover:text-zinc-200",
                            ].join(" ")}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
                <FsBrowser agentId={agent.id} kind={kind} key={kind} />
            </DialogContent>
        </Dialog>
    );
}

function NasCardBody({ agent }) {
    const nas = agent.nas || {};
    const vendor = nas.vendor_hint || "NAS storage";
    const proto = (nas.protocol || "").toUpperCase();
    const displayPath = nas.protocol === "smb"
        ? `\\\\${nas.host}\\${(nas.share || "").replace(/^[\\/]+/, "")}`
        : `${nas.host}:${nas.share?.startsWith("/") ? nas.share : "/" + (nas.share || "")}`;
    const credLabel = nas.has_password ? "stored" : (nas.credential_ref ? "vault ref" : "no creds");
    const capLabel = nas.bandwidth_limit_mbps ? `${nas.bandwidth_limit_mbps} MB/s` : "unlimited";
    const summary = [credLabel, capLabel, nas.username].filter(Boolean).join(" · ");
    return (
        <div className="space-y-2" data-testid={`nas-card-${agent.id}`}>
            <div className="flex items-start gap-2 rounded-md border border-[#00C2FF]/25 bg-gradient-to-br from-[#00C2FF]/5 to-transparent px-2 py-1.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-[#00C2FF]/15 text-[#00C2FF]">
                    <HardDrive size={13} />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                        <span className="truncate text-[12.5px] font-semibold">{vendor}</span>
                        {nas.mount_mode === "ro" && <span className="shrink-0 rounded-sm border border-border px-1 py-0.5 text-[8.5px] uppercase text-muted-foreground">ro</span>}
                    </div>
                    <div
                        className="truncate font-mono text-[10px] text-muted-foreground"
                        title={displayPath}
                        data-testid={`nas-path-${agent.id}`}
                    >
                        {displayPath}
                    </div>
                </div>
            </div>
            <div
                className="truncate font-mono text-[10px] text-muted-foreground"
                title={`${proto} · ${summary} · ${agent.os} · last heartbeat ${agent.last_heartbeat || "—"}`}
            >
                {proto}
                <span className="mx-1 text-border">·</span>
                {summary}
            </div>
            <div className="truncate font-mono text-[10px] text-muted-foreground">
                {agent.os}
                <span className="mx-1 text-border">·</span>
                {(agent.last_heartbeat || "").slice(11, 19) || "—"}
            </div>
        </div>
    );
}

function Metric({ label, value, accent, testId }) {
    const pct = Math.min(100, Math.max(0, value || 0));
    const barColor = accent ? "bg-[hsl(var(--accent))]" : "bg-brand";
    return (
        <div className="min-w-0 space-y-1" data-testid={testId}>
            <div className="flex items-baseline justify-between gap-1">
                <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">{label}</span>
                <span className="font-mono text-[10px] tabular-nums text-foreground">{pct.toFixed(0)}%</span>
            </div>
            <div className="h-[3px] overflow-hidden rounded-full bg-border">
                <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

function AgentAction({ label, icon: Icon, onClick, testId, positive, danger }) {
    const cls = danger
        ? "hover:border-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/10"
        : positive
          ? "hover:border-[hsl(var(--success))] hover:text-[hsl(var(--success))] hover:bg-[hsl(var(--success))]/10"
          : "hover:border-foreground/40 hover:bg-foreground/5";
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            title={label}
            aria-label={label}
            className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors ${cls}`}
        >
            <Icon size={12} />
        </button>
    );
}

function CreateAgentModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        name: "",
        role: "source",
        kind: "vm",
        hostname: "",
        os: "Linux",
        version: "1.0.0",
        listen_port: 9443,
        public_address: "",
        tls_fingerprint: "",
        bucket: "",
        region: "",
        prefix: "",
        credential_ref: "",
        // NAS fields
        nas_protocol: "smb",
        nas_host: "",
        nas_share: "",
        nas_mount_point: "/mnt/moovbytes",
        nas_username: "",
        nas_options: "",
        // Azure fields
        azure_account: "",
        azure_container: "",
        azure_sas_token: "",
        azure_prefix: "",
    });
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setErr("");
        try {
            const payload = {
                name: form.name,
                role: form.role,
                kind: form.kind,
            };
            if (form.kind === "vm") {
                payload.hostname = form.hostname || undefined;
                payload.os = form.os;
                payload.version = form.version;
                payload.listen_port = Number(form.listen_port) || 9443;
                if (form.public_address) payload.public_address = form.public_address;
                if (form.tls_fingerprint) payload.tls_fingerprint = form.tls_fingerprint;
            } else if (form.kind === "nas") {
                if (!form.nas_host || !form.nas_share) {
                    setErr("NAS host and share/export path are required");
                    setBusy(false);
                    return;
                }
                payload.hostname = form.hostname || undefined;
                payload.listen_port = Number(form.listen_port) || 9443;
                payload.version = "1.0.0-nas";
                payload.nas = {
                    protocol: form.nas_protocol,
                    host: form.nas_host,
                    share: form.nas_share,
                    mount_point: form.nas_mount_point || "/mnt/moovbytes",
                    username: form.nas_username || undefined,
                    credential_ref: form.credential_ref || undefined,
                    options: form.nas_options || undefined,
                };
            } else if (form.kind === "azure") {
                if (!form.azure_account || !form.azure_container) {
                    setErr("Storage account and container are required for Azure");
                    setBusy(false);
                    return;
                }
                payload.version = "1.0.0-azure";
                payload.azure = {
                    storage_account: form.azure_account,
                    container: form.azure_container,
                    sas_token: form.azure_sas_token || undefined,
                    prefix: form.azure_prefix || undefined,
                };
            } else {
                if (!form.bucket) {
                    setErr("Bucket name is required for cloud agents");
                    setBusy(false);
                    return;
                }
                payload.bucket = form.bucket;
                if (form.region) payload.region = form.region;
                if (form.prefix) payload.prefix = form.prefix;
                if (form.credential_ref) payload.credential_ref = form.credential_ref;
                payload.version = "1.0.0-cloud";
            }
            const { data } = await http.post("/agents", payload);
            onCreated(data);
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setBusy(false);
        }
    };

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const isCloud = form.kind === "s3" || form.kind === "gcs";
    const isAzure = form.kind === "azure";
    const isNas = form.kind === "nas";

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog">
            <form
                onSubmit={submit}
                className="w-full max-w-lg space-y-3 border border-border bg-surface p-5"
                data-testid="agent-create-modal"
            >
                <div className="flex items-center justify-between">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            Manual entry
                        </div>
                        <div className="font-display text-xl font-bold">Add a device record</div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-sm border border-border p-1.5 hover:border-foreground/40"
                    >
                        <X size={13} />
                    </button>
                </div>
                <p className="text-xs text-muted-foreground">
                    Creates a device record without installing anything — useful for planning a
                    migration before the real hardware is available. To connect a real PC, server,
                    or NAS, download and run the actual agent instead.
                </p>

                <label className="block">
                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Name
                    </span>
                    <input
                        value={form.name}
                        onChange={set("name")}
                        required
                        data-testid="agent-name-input"
                        className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                    />
                </label>

                <div>
                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Kind
                    </span>
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
                        {[
                            { v: "vm", label: "VM agent", icon: Server, color: "#5b5ef0" },
                            { v: "nas", label: "NAS (SMB/NFS)", icon: HardDrive, color: "#00C2FF" },
                            { v: "s3", label: "Amazon S3", icon: Cloud, color: "#ff9900" },
                            { v: "gcs", label: "Google GCS", icon: Cloud, color: "#4285F4" },
                            { v: "azure", label: "Azure Blob", icon: Cloud, color: "#0078D4" },
                        ].map((k) => {
                            const KindI = k.icon;
                            const active = form.kind === k.v;
                            return (
                                <button
                                    key={k.v}
                                    type="button"
                                    onClick={() => setForm({ ...form, kind: k.v })}
                                    data-testid={`agent-kind-${k.v}`}
                                    className={`flex items-center gap-2 rounded-md border px-3 py-2 text-[11px] font-medium transition-colors ${
                                        active
                                            ? "border-foreground bg-foreground/5"
                                            : "border-border hover:border-foreground/40"
                                    }`}
                                >
                                    <KindI size={13} style={{ color: k.color }} /> {k.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                <label className="block">
                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Role
                    </span>
                    <select
                        value={form.role}
                        onChange={set("role")}
                        data-testid="agent-role-input"
                        className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                    >
                        <option value="source">source</option>
                        <option value="destination">destination</option>
                    </select>
                </label>

                {isCloud ? (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Bucket *
                                </span>
                                <input
                                    value={form.bucket}
                                    onChange={set("bucket")}
                                    placeholder={form.kind === "s3" ? "my-prod-bucket" : "my-gcs-bucket"}
                                    required
                                    data-testid="agent-bucket-input"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </label>
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Region
                                </span>
                                <input
                                    value={form.region}
                                    onChange={set("region")}
                                    placeholder={form.kind === "s3" ? "us-east-1" : "us-central1"}
                                    data-testid="agent-region-input"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </label>
                        </div>
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Prefix (optional)
                            </span>
                            <input
                                value={form.prefix}
                                onChange={set("prefix")}
                                placeholder="datasets/2026/"
                                data-testid="agent-prefix-input"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Credential reference (vault path / env key — never the secret)
                            </span>
                            <input
                                value={form.credential_ref}
                                onChange={set("credential_ref")}
                                placeholder={form.kind === "s3" ? "vault:secret/aws/prod" : "vault:secret/gcp/prod"}
                                data-testid="agent-credential-ref-input"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </label>
                        <div className="rounded-md border border-[#5b5ef0]/30 bg-[#5b5ef0]/5 p-3 font-mono text-[10px] leading-relaxed text-muted-foreground">
                            MoovBytes stores only a <b>reference</b> to your cloud credentials (vault path
                            or env key). The actual secret never leaves your agent machine.
                        </div>
                    </>
                ) : isAzure ? (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Storage account *
                                </span>
                                <input
                                    value={form.azure_account}
                                    onChange={set("azure_account")}
                                    placeholder="mystorageaccount"
                                    required
                                    data-testid="agent-azure-account-input"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </label>
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Container *
                                </span>
                                <input
                                    value={form.azure_container}
                                    onChange={set("azure_container")}
                                    placeholder="backups"
                                    required
                                    data-testid="agent-azure-container-input"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </label>
                        </div>
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Prefix (optional — scopes this connection to a folder within the container)
                            </span>
                            <input
                                value={form.azure_prefix}
                                onChange={set("azure_prefix")}
                                placeholder="datasets/2026/"
                                data-testid="agent-azure-prefix-input"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                SAS token
                            </span>
                            <input
                                value={form.azure_sas_token}
                                onChange={set("azure_sas_token")}
                                type="password"
                                autoComplete="new-password"
                                placeholder="sv=2024-...&sig=..."
                                data-testid="agent-azure-sas-input"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </label>
                        <div className="rounded-md border border-[#0078D4]/30 bg-[#0078D4]/5 p-3 font-mono text-[10px] leading-relaxed text-muted-foreground">
                            Generate a <b>Shared Access Signature</b> scoped to this container from the Azure
                            Portal (Container → Shared access tokens) with Read/List/Write permissions as
                            needed. It's encrypted at rest and only ever decrypted in-memory on whichever
                            agent you pick to do the connecting — never a full storage-account key.
                        </div>
                    </>
                ) : isNas ? (
                    <>
                        <div className="grid grid-cols-3 gap-3">
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Protocol *
                                </span>
                                <select
                                    value={form.nas_protocol}
                                    onChange={set("nas_protocol")}
                                    data-testid="agent-nas-protocol"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                                >
                                    <option value="smb">SMB / CIFS</option>
                                    <option value="nfs">NFS</option>
                                </select>
                            </label>
                            <label className="col-span-2 block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    NAS host *
                                </span>
                                <input
                                    value={form.nas_host}
                                    onChange={set("nas_host")}
                                    placeholder="synology.local or 10.0.0.20"
                                    required
                                    data-testid="agent-nas-host"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </label>
                        </div>
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                {form.nas_protocol === "smb" ? "Share name *" : "Export path *"}
                            </span>
                            <input
                                value={form.nas_share}
                                onChange={set("nas_share")}
                                placeholder={form.nas_protocol === "smb" ? "backups  (maps to \\\\host\\backups)" : "/volume1/backups"}
                                required
                                data-testid="agent-nas-share"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Mount point
                                </span>
                                <input
                                    value={form.nas_mount_point}
                                    onChange={set("nas_mount_point")}
                                    placeholder="/mnt/moovbytes"
                                    data-testid="agent-nas-mount"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </label>
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Mount options (optional)
                                </span>
                                <input
                                    value={form.nas_options}
                                    onChange={set("nas_options")}
                                    placeholder={form.nas_protocol === "smb" ? "vers=3.0,sec=ntlmssp" : "vers=3,rw"}
                                    data-testid="agent-nas-options"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                                />
                            </label>
                        </div>
                        {form.nas_protocol === "smb" && (
                            <div className="grid grid-cols-2 gap-3">
                                <label className="block">
                                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Username
                                    </span>
                                    <input
                                        value={form.nas_username}
                                        onChange={set("nas_username")}
                                        placeholder="moov"
                                        data-testid="agent-nas-username"
                                        className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                                    />
                                </label>
                                <label className="block">
                                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                        Credential reference
                                    </span>
                                    <input
                                        value={form.credential_ref}
                                        onChange={set("credential_ref")}
                                        placeholder="vault:secret/nas/syn1"
                                        data-testid="agent-nas-credential-ref"
                                        className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                                    />
                                </label>
                            </div>
                        )}
                        <div className="rounded-md border border-[#00C2FF]/30 bg-[#00C2FF]/5 p-3 font-mono text-[10px] leading-relaxed text-muted-foreground">
                            The MoovBytes agent runs on a Linux/Windows VM and mounts your NAS share at
                            <b> {form.nas_mount_point || "/mnt/moovbytes"}</b>. The NAS password never
                            leaves that VM — only a <b>reference</b> (vault path / env key) is stored
                            by MoovBytes.
                        </div>
                    </>
                ) : (
                    <>
                        <div className="grid grid-cols-2 gap-3">
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    OS
                                </span>
                                <input
                                    value={form.os}
                                    onChange={set("os")}
                                    data-testid="agent-os-input"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                                />
                            </label>
                            <label className="block">
                                <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Port
                                </span>
                                <input
                                    type="number"
                                    min={1}
                                    max={65535}
                                    value={form.listen_port}
                                    onChange={set("listen_port")}
                                    data-testid="agent-listen-port-input"
                                    className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                                />
                            </label>
                        </div>
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Hostname
                            </span>
                            <input
                                value={form.hostname}
                                onChange={set("hostname")}
                                placeholder="src.dc1.example.com"
                                data-testid="agent-hostname-input"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                Public address (optional — override for NAT / VPN)
                            </span>
                            <input
                                value={form.public_address}
                                onChange={set("public_address")}
                                placeholder="src.dc1.example.com:9443"
                                data-testid="agent-public-address-input"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </label>
                        <label className="block">
                            <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                                TLS fingerprint (SHA-256, optional for pinning)
                            </span>
                            <input
                                value={form.tls_fingerprint}
                                onChange={set("tls_fingerprint")}
                                placeholder="a1:b2:c3:…"
                                data-testid="agent-tls-input"
                                className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 font-mono text-xs"
                            />
                        </label>
                    </>
                )}

                <label className="block">
                    <span className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        Version
                    </span>
                    <input
                        value={form.version}
                        onChange={set("version")}
                        data-testid="agent-version-input"
                        className="mb-focus w-full rounded-sm border border-input bg-background px-3 py-2 text-sm"
                    />
                </label>

                {err && (
                    <div className="rounded-sm border border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/5 px-3 py-2 text-xs text-[hsl(var(--destructive))]">
                        {err}
                    </div>
                )}
                <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-sm border border-border px-3 py-2 text-xs font-semibold uppercase tracking-widest"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="agent-create-submit"
                        className="rounded-sm bg-brand px-3 py-2 text-xs font-semibold uppercase tracking-widest text-brand-foreground hover:bg-brand-dark disabled:opacity-60"
                    >
                        {busy ? "Creating…" : "Create"}
                    </button>
                </div>
            </form>
        </div>
    );
}
